/**
 * Drop-in for @tabler/icons-react's internal `createReactComponent`, swapped
 * in by the web app, the landing site and admin at build time (website and
 * admin: `resolve.alias` + dev pre-bundle plugin in vite.config.ts; landing:
 * a webpack replacement in gatsby-node.ts).
 *
 * Why: Tabler draws each outline icon as several separate <path>s. With a
 * see-through color (`text-white/50` and friends) every path is painted on
 * its own, so wherever two pieces meet or cross the strokes stack and the
 * spot looks brighter (graph icon, plus, x, sidebar, school cap; Dan,
 * 2026-10-15). Merging the plain paths into one <path> makes the browser
 * stroke them as a single shape, painted once, so the icon is one even color
 * at any opacity. Opaque colors look exactly the same as before.
 *
 * Only plain paths (`d` + `key`) merge; the few nodes with their own fill,
 * stroke or opacity stay separate. Filled icons are left alone (merging
 * could change which areas count as holes).
 *
 * Plain JS (types in the sibling .d.ts) so Gatsby's webpack can take it
 * as-is. Keep in sync with node_modules/@tabler/icons-react/dist/esm/
 * createReactComponent.mjs when bumping the package.
 */
import { createElement, forwardRef } from "react";

const DEFAULT_ATTRIBUTES = {
  outline: {
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  },
  filled: {
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    stroke: "none",
  },
};

function isPlainPath([tag, attrs]) {
  if (tag !== "path") return false;
  for (const key in attrs) {
    if (key !== "d" && key !== "key") return false;
  }
  return true;
}

/** Folds runs of plain paths into one path so overlaps paint once. */
export function mergeOutlinePaths(iconNode) {
  const merged = [];
  let run = [];
  const flush = () => {
    if (run.length === 0) return;
    merged.push(["path", { d: run.join(" "), key: `svg-merged-${merged.length}` }]);
    run = [];
  };
  for (const node of iconNode) {
    if (isPlainPath(node)) {
      run.push(String(node[1].d));
    } else {
      flush();
      merged.push(node);
    }
  }
  flush();
  return merged;
}

const createReactComponent = (type, iconName, iconNamePascal, iconNode) => {
  const nodes = type === "outline" ? mergeOutlinePaths(iconNode) : iconNode;
  const Component = forwardRef(
    ({ color = "currentColor", size = 24, stroke = 2, title, className, children, ...rest }, ref) =>
      createElement(
        "svg",
        {
          ref,
          ...DEFAULT_ATTRIBUTES[type],
          width: size,
          height: size,
          className: [`tabler-icon`, `tabler-icon-${iconName}`, className].join(" "),
          ...(type === "filled" ? { fill: color } : { strokeWidth: stroke, stroke: color }),
          ...rest,
        },
        [
          title && createElement("title", { key: "svg-title" }, title),
          ...nodes.map(([tag, attrs]) => createElement(tag, attrs)),
          ...(Array.isArray(children) ? children : [children]),
        ],
      ),
  );
  Component.displayName = iconNamePascal;
  return Component;
};

export default createReactComponent;
